import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";

const BUILT_INS = ["gdpr", "nis2", "iso27001", "dora", "ai_act"];
const BUILT_IN_LABELS = { gdpr: "GDPR", nis2: "NIS2", iso27001: "ISO 27001", dora: "DORA", ai_act: "AI Act" };
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const newItem = () => ({ _key: uid(), code: "", title: "", description: "", weight: 3, reference: "" });
const emptySection = () => ({ _key: uid(), title: "", items: [newItem()] });

export default function TemplateEditor() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isNew = type === "nuovo" || location.pathname.endsWith("/template/nuovo");
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("gdpr");
  const [tagCatalog, setTagCatalog] = useState([]);
  const [industryTags, setIndustryTags] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [overrideOf, setOverrideOf] = useState("");
  const [sections, setSections] = useState([emptySection()]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [isBuiltIn, setIsBuiltIn] = useState(false);

  useEffect(() => {
    api.get("/company-tags").then((r) => setTagCatalog(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) {
      if (searchParams.get("category")) setCategory(searchParams.get("category"));
      if (searchParams.get("name")) setName(searchParams.get("name"));
      if (searchParams.get("industry_tag")) setIndustryTags([searchParams.get("industry_tag")]);
    }
  }, [isNew, searchParams]);

  useEffect(() => {
    if (isNew || type === "nuovo") return;
    api.get(`/templates/${type}`).then((r) => {
      const t = r.data;
      setName(t.name || "");
      setDescription(t.description || "");
      setCategory(t.category || t.override_of || "custom");
       setIndustryTags(t.industry_tags || []);
       setIsDefault(Boolean(t.is_default));
      setOverrideOf(t.override_of || "");
      setSections((t.sections || []).map((s) => ({
        _key: uid(),
        title: s.title,
        items: (s.items || []).map((it) => ({
          _key: uid(),
          code: it.code || "", title: it.title || "",
          description: it.description || "", weight: it.weight || 3,
          reference: it.reference || "",
        })),
      })));
      setTemplateId(t.template_id);
      setIsBuiltIn(!t.custom);
      setLoading(false);
    }).catch(() => { toast.error("Template non trovato"); navigate("/app/template"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const setSection = (i, patch) => setSections((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const setItem = (si, ii, patch) => setSections((prev) => prev.map((s, i) => i !== si ? s : ({
    ...s, items: s.items.map((it, j) => j === ii ? { ...it, ...patch } : it)
  })));
  const addSection = () => setSections((prev) => [...prev, emptySection()]);
  const removeSection = (i) => setSections((prev) => prev.filter((_, idx) => idx !== i));
  const addItem = (si) => setSections((prev) => prev.map((s, i) => i !== si ? s : ({
    ...s, items: [...s.items, newItem()]
  })));
  const removeItem = (si, ii) => setSections((prev) => prev.map((s, i) => i !== si ? s : ({
    ...s, items: s.items.filter((_, j) => j !== ii)
  })));

  const save = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome per il template"); return; }
    const validSections = sections.filter((s) => s.title.trim() && s.items.some((it) => it.title.trim()));
    if (!validSections.length) { toast.error("Aggiungi almeno una sezione con un requisito"); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      category: category.trim(),
      industry_tags: industryTags,
      is_default: isDefault,
      override_of: overrideOf || null,
      sections: sections.map((s) => ({
        title: s.title,
        items: s.items.filter((it) => it.title.trim()).map((it) => ({
          code: it.code, title: it.title, description: it.description,
          weight: Number(it.weight) || 3, reference: it.reference,
        })),
      })).filter((s) => s.title.trim() && s.items.length),
    };
    try {
      if (isNew || (isBuiltIn && !templateId)) {
        await api.post("/templates", payload);
        toast.success("Template creato");
      } else {
        await api.put(`/templates/${templateId}`, payload);
        toast.success("Template aggiornato");
      }
      navigate("/app/template");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio template"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-slate-500">Caricamento…</div>;

  const totalItems = sections.reduce((n, s) => n + s.items.filter((it) => it.title.trim()).length, 0);

  return (
    <div className="p-6 md:p-10 max-w-5xl pb-24">
      <div className="mb-8 flex items-center gap-3">
        <Link to="/app/template" className="text-slate-500 hover:text-slate-900 transition-colors duration-200"><ArrowLeft className="h-5 w-5" /></Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Template audit</div>
          <h1 className="font-display font-black text-3xl md:text-4xl">{isNew ? "Nuovo template" : "Modifica template"}</h1>
          <div className="text-xs text-slate-500 mt-1 font-mono-plex">{sections.length} sezioni · {totalItems} controlli</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Nome</Label>
            <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11" placeholder="Es. Audit interno GDPR 2026" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Sostituisce un built-in? (opzionale)</Label>
            <Select value={overrideOf || "none"} onValueChange={(v) => setOverrideOf(v === "none" ? "" : v)} disabled={!isNew && !!templateId && !isBuiltIn}>
              <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No, template autonomo</SelectItem>
                {BUILT_INS.map((k) => <SelectItem key={k} value={k}>Sostituisci {k.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600">Descrizione</Label>
          <Textarea data-testid="template-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-2" placeholder="Note interne, riferimenti…" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600">Categoria / normativa</Label>
          <Input list="template-categories" data-testid="template-category-input" value={category} onChange={(e) => setCategory(e.target.value)} disabled={!isNew && isBuiltIn} className="mt-2 h-11" placeholder="Es. GDPR oppure Privacy interna" />
          <datalist id="template-categories">
            {BUILT_INS.map((key) => <option key={key} value={key}>{BUILT_IN_LABELS[key]}</option>)}
          </datalist>
          <div className="mt-1.5 text-[11px] text-slate-500">Scegli una normativa esistente o inserisci una categoria libera.</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600">Settori aziendali</Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            {tagCatalog.map((tag) => {
              const selected = industryTags.includes(tag.name);
              return <button key={tag.id} type="button" data-testid={`template-industry-tag-${tag.id}`} onClick={() => setIndustryTags((current) => selected ? current.filter((value) => value !== tag.name) : [...current, tag.name])} className={`rounded-md border px-3 py-2 text-left text-xs transition-colors ${selected ? "border-blue-500 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`} aria-pressed={selected}>{selected ? "✓ " : ""}{tag.name}</button>;
            })}
          </div>
          <div className="mt-1.5 text-[11px] text-slate-500">Se non selezioni settori, la checklist sarà disponibile in Generale.</div>
        </div>
        <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-amber-50/50 px-3 py-3 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 accent-amber-600" />
          <span><strong>Checklist standard</strong><span className="block text-xs text-slate-500">Può esserci una sola checklist standard per ogni normativa e TAG.</span></span>
        </label>
      </div>

      <div className="space-y-4">
        {sections.map((s, si) => (
          <div key={s._key} data-testid={`section-${si}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <span className="font-mono-plex text-xs text-slate-500">Sezione {si + 1}</span>
              <Input data-testid={`section-title-${si}`} value={s.title} onChange={(e) => setSection(si, { title: e.target.value })} placeholder="Titolo sezione (es. Governance)" className="h-9 font-semibold" />
              {sections.length > 1 && (
                <button onClick={() => removeSection(si)} data-testid={`section-remove-${si}`} className="p-2 text-slate-400 hover:text-red-600 rounded-md transition-colors duration-200">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="p-3 space-y-2">
              {s.items.map((it, ii) => (
                <div key={it._key} className="border border-slate-200 rounded-md p-3 hover:border-slate-300 transition-colors duration-200" data-testid={`item-${si}-${ii}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <Input data-testid={`item-code-${si}-${ii}`} value={it.code} onChange={(e) => setItem(si, ii, { code: e.target.value })} placeholder="Cod." className="col-span-12 sm:col-span-2 h-9 font-mono-plex text-xs" />
                    <Input data-testid={`item-title-${si}-${ii}`} value={it.title} onChange={(e) => setItem(si, ii, { title: e.target.value })} placeholder="Requisito / Voce di controllo" className="col-span-12 sm:col-span-7 h-9" />
                    <Input type="number" min={1} max={5} data-testid={`item-weight-${si}-${ii}`} value={it.weight} onChange={(e) => setItem(si, ii, { weight: Number(e.target.value) })} placeholder="Peso" className="col-span-10 sm:col-span-2 h-9" />
                    <button onClick={() => removeItem(si, ii)} data-testid={`item-remove-${si}-${ii}`} className="col-span-2 sm:col-span-1 p-2 text-slate-400 hover:text-red-600 rounded-md transition-colors duration-200"><Trash2 className="h-4 w-4 mx-auto" /></button>
                  </div>
                  <div className="grid grid-cols-12 gap-2 mt-2">
                    <Input value={it.reference} onChange={(e) => setItem(si, ii, { reference: e.target.value })} placeholder="Riferimento normativo (opzionale)" className="col-span-12 sm:col-span-4 h-9 text-xs" />
                    <Input value={it.description} onChange={(e) => setItem(si, ii, { description: e.target.value })} placeholder="Descrizione / spiegazione (opzionale)" className="col-span-12 sm:col-span-8 h-9 text-xs" />
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addItem(si)} data-testid={`add-item-${si}`} className="h-8 text-xs"><Plus className="h-3 w-3" /> Aggiungi requisito</Button>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addSection} data-testid="add-section-btn" className="w-full h-11 border-dashed"><Plus className="h-4 w-4" /> Nuova sezione</Button>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 md:ml-64 px-6 py-3 flex justify-end gap-3 z-40">
        <Link to="/app/template"><Button variant="outline" className="h-10">Annulla</Button></Link>
        <Button onClick={save} disabled={saving} data-testid="save-template-btn" className="h-10 bg-slate-900 hover:bg-slate-800 px-6">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva template"}
        </Button>
      </div>
    </div>
  );
}
